import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ButtonComponent } from '../../atoms/button/button.component';
import { Store } from '@ngrx/store';
import { completeOnboarding } from '../../../../core/state/user/user.actions';
import { ButtonSize, ButtonSkin, ButtonType } from '../../../enums/button.enum';
import { UnitSystem } from '../../../enums/unitSystem.enum';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { IconComponent } from '../../atoms/icon/icon.component';
import { IconSize } from '../../../enums/icon.enum';
import { LanguageService } from '../../../../core/services/language.service';
import { TranslateModule } from '@ngx-translate/core';
import { selectIngredientBriefLoading, selectIngredientBriefResultsWithDisplayName } from '../../../../core/state/ingredient/ingredient.selectors';
import { selectStorageQuota } from '../../../../core/state/storage/storage.selector';
import { loadEssentialIngredientsBrief } from '../../../../core/state/ingredient/ingredient.actions';
import { bulkAddStorageItems, loadStorageQuota } from '../../../../core/state/storage/storage.actions';
import { map, take } from 'rxjs';
import { IngredientBrief, Ingredient } from '../../../models/ingredient.model';
import { suggestStorageDefaults } from '../../../utils/storage-defaults.util';
import { getUnitsForSystem, Unit } from '../../../enums/unit.enum';
import { IngredientLocation } from '../../../enums/ingredient-location.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpinnerComponent } from '../../atoms/spinner/spinner.component';
import { getUserUnitSystem, getUnitLabel, setUserUnitSystem } from '../../../utils/unit-settings.util';
import { TranslateService } from '@ngx-translate/core';
import { isQuotaEnforced } from '../../../models/quota-info.model';

@Component({
  selector: 'app-onboarding-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    ButtonComponent,
    MatFormFieldModule, 
    MatInputModule, 
    MatSelectModule,
    MatOptionModule,
    MatRadioModule,
    SpinnerComponent,
    IconComponent,
    TranslateModule
  ],
  templateUrl: './onboarding-dialog.component.html',
  styleUrl: './onboarding-dialog.component.scss'
})
export class OnboardingDialogComponent {
  currentStep = 1;
  maxStep = 12;

  private store = inject(Store);
  private fb = inject(FormBuilder);
  private languageService = inject(LanguageService);
  private readonly minStorageSelection = 5;

  public ButtonSkin = ButtonSkin;
  public ButtonSize = ButtonSize;
  public ButtonType = ButtonType;
  public IconSize = IconSize;

  public preferencesForm: FormGroup;
  public units = Object.values(UnitSystem);
  public languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'hu', label: 'Hungarian' }
  ];

  public essentials$ = this.store.select(selectIngredientBriefResultsWithDisplayName);
  public essentialsLoading$ = this.store.select(selectIngredientBriefLoading);
  public storageItemsForm: FormGroup;

  private selectedStorageIds = new Set<number>();
  public quotaEnforced = false;
  private remainingSlots = Number.POSITIVE_INFINITY;

  constructor(
    private dialogRef: MatDialogRef<OnboardingDialogComponent>,
    private translate: TranslateService,
  ) {
    const currentLang = this.languageService.getLanguage() || 'en';

    this.preferencesForm = this.fb.group({
      preferred_language: [currentLang, Validators.required],
      preferred_unit_system: [getUserUnitSystem(), Validators.required],
      preferred_dietary: [[]],
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/)]]
    });

    this.storageItemsForm = this.fb.group({
      items: this.fb.array([])
    });

    this.store.dispatch(loadEssentialIngredientsBrief());
    this.store.dispatch(loadStorageQuota());

    this.store.select(selectStorageQuota)
      .pipe(takeUntilDestroyed())
      .subscribe((quota) => {
        if (!quota) return;
        this.quotaEnforced = isQuotaEnforced(quota);
        this.remainingSlots = this.quotaEnforced
          ? Math.max(quota.limit - quota.used, 0)
          : Number.POSITIVE_INFINITY;
      });
  }

  get itemsFA(): FormArray {
    return this.storageItemsForm.get('items') as FormArray;
  }

  get maxSelectable(): number {
    return this.remainingSlots;
  }

  get selectedCount(): number {
    return this.selectedStorageIds.size;
  }

  get canPickMore(): boolean {
    return this.selectedStorageIds.size < this.maxSelectable;
  }

  get canProceedStorageSelection(): boolean {
    return this.selectedStorageIds.size >= this.minStorageSelection && this.selectedStorageIds.size <= this.maxSelectable;
  }

  get selectedUnitsForStorageStep(): Unit[] {
    const selectedUnitSystem = this.preferencesForm.get('preferred_unit_system')?.value as UnitSystem;
    return getUnitsForSystem(selectedUnitSystem);
  }

  getStorageUnitLabel(unit: Unit): string {
    return getUnitLabel(unit, this.translate);
  }

  isStorageSelected(id: number): boolean {
    return this.selectedStorageIds.has(id);
  }

  toggleStorageSelection(id: number): void {
    if (this.selectedStorageIds.has(id)) {
      this.selectedStorageIds.delete(id);
      return;
    }

    if (!this.canPickMore) {
      return;
    }

    this.selectedStorageIds.add(id);
  }

  updateStorageQuantity(index: number, value: string | number): void {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return;
    }

    this.itemsFA.at(index).get('quantity')?.setValue(numericValue);
  }

  updateStorageUnit(index: number, value: Unit): void {
    this.itemsFA.at(index).get('unit')?.setValue(value);
  }

  private getStorageItemsPayload() {
    return this.itemsFA.value.map((item) => ({
      ingredient_id: item.ingredient_id as number,
      leftover_id: null,
      quantity: Number(item.quantity),
      unit: item.unit as Unit,
      location: IngredientLocation.Pantry,
    }));
  }

  private buildStorageItemsStep(): void {
    const chosen = new Set(this.selectedStorageIds);

    this.essentials$
      .pipe(
        take(1),
        map(list => list.filter(ingredient => chosen.has(ingredient.ingredient_id)))
      )
      .subscribe((picked: (IngredientBrief & { displayName?: string; icon?: string })[]) => {
        while (this.itemsFA.length) this.itemsFA.removeAt(0);

        picked.forEach((brief) => {
          const syntheticIngredient: Partial<Ingredient> = {
            ingredient_id: brief.ingredient_id,
            name: brief.name,
            hu_name: brief.hu_name ?? undefined,
            icon: brief.icon ?? undefined,
            category: (brief.category ?? undefined) as any,
            default_location: brief.default_location ?? IngredientLocation.Pantry,
          };

          const defaults = suggestStorageDefaults(syntheticIngredient as Ingredient);

          this.itemsFA.push(this.fb.group({
            ingredient_id: [brief.ingredient_id, [Validators.required]],
            name: [brief.displayName || brief.hu_name || brief.name],
            icon: [brief.icon ?? 'food-variant'],
            quantity: [defaults.quantity, [Validators.required, Validators.min(0.01)]],
            unit: [defaults.unit, [Validators.required]],
          }));
        });
      });
  }

  nextStep(): void {
    const langControl = this.preferencesForm.get('preferred_language');
    const nameControl = this.preferencesForm.get('name');

    if ((this.currentStep === 2 && langControl?.invalid) ||
        (this.currentStep === 3 && nameControl?.invalid)) {
      nameControl?.markAsTouched();
      return;
    }

    if (this.currentStep === 2 && langControl?.valid) {
      const selectedLang = langControl.value;
      this.languageService.setLanguage(selectedLang);
    }

    if (this.currentStep === 4) {
      const selectedUnitSystem = this.preferencesForm.get('preferred_unit_system')?.value as UnitSystem;
      setUserUnitSystem(selectedUnitSystem);
    }

    if (this.currentStep === 5) {
      if (!this.canProceedStorageSelection) return;
      this.buildStorageItemsStep();
      this.currentStep = 6;
      return;
    }

    if (this.currentStep === 6) {
      if (this.storageItemsForm.invalid) return;

      this.currentStep = 7;
      return;
    }

    if (this.currentStep < this.maxStep) {
      this.currentStep = this.currentStep + 1;
    } else {
      if (this.itemsFA.length > 0) {
        const items = this.getStorageItemsPayload();
        this.store.dispatch(bulkAddStorageItems({ items }));
      }

      const { preferred_language, preferred_unit_system, name } = this.preferencesForm.value;
      this.store.dispatch(completeOnboarding({
        preferred_language,
        preferred_unit_system,
        preferred_dietary: [],
        name: name?.trim(),
        onboarding_complete: true
      }));
      this.dialogRef.close();
    }
  }


  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep = this.currentStep - 1;
    }
  }

  getStepTitle(step: number): string {
    switch (step) {
      case 1: return 'onboarding.titles.step1';
      case 2: return 'onboarding.titles.step2';
      case 3: return 'onboarding.titles.step3';
      case 4: return 'onboarding.titles.step4';
      case 5: return 'onboarding.titles.step5';
      case 6: return 'onboarding.titles.step6';
      case 7: return 'onboarding.titles.step7';
      case 8: return 'onboarding.titles.step8';
      case 9: return 'onboarding.titles.step9';
      case 10: return 'onboarding.titles.step10';
      case 11: return 'onboarding.titles.step11';
      case 12: return 'onboarding.titles.step12';
      default: return '';
    }
  }

}
